import type { ErrorReason } from "@app/core/error-catalog";
import type { FieldRule, FieldIssueParams } from "@app/core/validation";
import type { Lang } from "../i18n.js";

/**
 * The localized error catalog: every stable server `reason` (and the client-only
 * transport reasons) mapped to a user-facing TR/EN message with an optional
 * corrective hint and a directing action.
 *
 * Typed as `Record<ErrorReason, ErrorMessage>` so adding a reason to
 * `@app/core`'s ERROR_REASONS without a message here fails `pnpm typecheck` —
 * coverage is guaranteed by the compiler, not by review.
 */

export type ErrorAction =
  | { kind: "retry"; label: string } // re-run the operation that failed
  | { kind: "reload"; label: string } // full page reload
  | { kind: "login"; label: string } // session ended → go to /login
  | { kind: "route"; to: string; label: string } // send the user somewhere useful
  | { kind: "support"; label: string }; // copy the correlation id

export interface ErrorMessage {
  /** One-line, human, specific. */
  title: string;
  /** What to do about it. Optional but strongly preferred. */
  hint?: string;
  /** A button that moves the user toward a fix. */
  action?: ErrorAction;
}

/* --- Reason catalog -------------------------------------------------------- */

const tr: Record<ErrorReason, ErrorMessage> = {
  // Request/transport
  VALIDATION_FAILED: {
    title: "Gönderdiğiniz bilgilerde hatalar var.",
    hint: "İşaretli alanları düzeltip tekrar deneyin.",
  },
  REQUEST_ROUTE_NOT_FOUND: { title: "İstenen adres bulunamadı." },
  REQUEST_BODY_MALFORMED: {
    title: "İstek verisi okunamadı.",
    hint: "Sayfayı yenileyip tekrar deneyin.",
    action: { kind: "reload", label: "Sayfayı yenile" },
  },
  REQUEST_PAYLOAD_TOO_LARGE: {
    title: "Gönderdiğiniz içerik çok büyük.",
    hint: "Daha küçük bir dosya veya daha az veri ile deneyin.",
  },
  REQUEST_UNSUPPORTED_MEDIA_TYPE: { title: "Bu içerik türü desteklenmiyor." },
  REQUEST_RATE_LIMITED: {
    title: "Çok fazla istek gönderildi.",
    hint: "Kısa bir süre bekleyip tekrar deneyin.",
    action: { kind: "retry", label: "Tekrar dene" },
  },
  REQUEST_CORS_REJECTED: { title: "İsteğin kaynağı bu sunucu tarafından kabul edilmiyor." },
  INTERNAL_ERROR: {
    title: "Beklenmeyen bir hata oluştu.",
    hint: "Sorun sürerse destek referansını paylaşın.",
    action: { kind: "reload", label: "Sayfayı yenile" },
  },

  // Authentication
  AUTH_REQUIRED: {
    title: "Bu işlem için giriş yapmalısınız.",
    action: { kind: "login", label: "Giriş yap" },
  },
  AUTH_INVALID_CREDENTIALS: {
    title: "E-posta veya parola hatalı.",
    hint: "Bilgilerinizi kontrol edip tekrar deneyin.",
  },
  AUTH_CURRENT_PASSWORD_INVALID: { title: "Mevcut parolanız hatalı." },
  AUTH_PASSWORD_INVALID: { title: "Parola hatalı." },
  AUTH_ACCOUNT_LOCKED: {
    title: "Hesabınız geçici olarak kilitlendi.",
    hint: "Çok sayıda hatalı denemeden dolayı. Bir süre sonra tekrar deneyin.",
  },
  AUTH_PASSWORD_REUSED: {
    title: "Bu parolayı yakın zamanda kullandınız.",
    hint: "Daha önce kullanmadığınız bir parola seçin.",
  },
  AUTH_SELF_REGISTRATION_DISABLED: {
    title: "Kendi kendine kayıt kapalı.",
    hint: "Hesap için organizasyon yöneticinizle iletişime geçin.",
  },
  AUTH_EMAIL_TAKEN: {
    title: "Bu e-posta ile bir hesap zaten var.",
    hint: "Giriş yapmayı deneyin veya farklı bir e-posta kullanın.",
  },
  AUTH_TOKEN_INVALID: {
    title: "Oturum bilgisi geçersiz.",
    hint: "Lütfen tekrar giriş yapın.",
    action: { kind: "login", label: "Giriş yap" },
  },
  AUTH_ACCESS_TOKEN_EXPIRED: {
    title: "Oturumunuzun süresi doldu.",
    action: { kind: "login", label: "Tekrar giriş yap" },
  },
  AUTH_REFRESH_TOKEN_UNKNOWN: {
    title: "Oturum tanınmadı.",
    hint: "Lütfen tekrar giriş yapın.",
    action: { kind: "login", label: "Giriş yap" },
  },
  AUTH_REFRESH_TOKEN_EXPIRED: {
    title: "Oturumunuzun süresi doldu.",
    action: { kind: "login", label: "Tekrar giriş yap" },
  },
  AUTH_SESSION_EXPIRED: {
    title: "Oturumunuz sona erdi.",
    hint: "Güvenlik için tekrar giriş yapmanız gerekiyor.",
    action: { kind: "login", label: "Tekrar giriş yap" },
  },
  AUTH_TOKEN_REUSE_DETECTED: {
    title: "Güvenlik nedeniyle oturumunuz sonlandırıldı.",
    hint: "Lütfen tekrar giriş yapın.",
    action: { kind: "login", label: "Giriş yap" },
  },
  AUTH_MFA_CODE_INVALID: {
    title: "Doğrulama kodu hatalı.",
    hint: "Kimlik doğrulama uygulamanızdaki 6 haneli kodu girin.",
  },
  AUTH_MFA_CHALLENGE_EXPIRED: {
    title: "Doğrulama süresi doldu.",
    hint: "Lütfen tekrar giriş yapın.",
    action: { kind: "login", label: "Giriş yap" },
  },
  AUTH_MFA_ENROLLMENT_REQUIRED: {
    title: "Bu işlem için iki adımlı doğrulama gerekiyor.",
    hint: "Profilinizden iki adımlı doğrulamayı kurun.",
    action: { kind: "route", to: "/profile", label: "Doğrulamayı kur" },
  },
  AUTH_MFA_ENROLLMENT_NOT_STARTED: { title: "Önce iki adımlı doğrulama kurulumunu başlatın." },
  AUTH_MFA_ALREADY_ENABLED: {
    title: "İki adımlı doğrulama zaten açık.",
    hint: "Yeniden kurmak için önce mevcut doğrulamayı kapatın.",
  },
  AUTH_PASSWORD_CHANGE_SESSION_EXPIRED: {
    title: "Parola değiştirme oturumu sona erdi.",
    action: { kind: "login", label: "Tekrar giriş yap" },
  },
  AUTH_DEMO_READ_ONLY: {
    title: "Bu bir salt-okunur demo hesabı.",
    hint: "Formları inceleyebilirsiniz ama değişiklikler kaydedilmez.",
  },
  AUTH_API_KEY_REQUIRED: { title: "Bir API anahtarı gerekiyor." },
  AUTH_API_KEY_INVALID: { title: "API anahtarı geçersiz." },
  AUTH_API_KEY_SCOPE_MISSING: { title: "API anahtarı bu işlem için gerekli yetkiye sahip değil." },

  // Authorization
  PERMISSION_DENIED: {
    title: "Bu işlem için yetkiniz yok.",
    hint: "Gerekli yetki için yöneticinizle görüşün.",
  },
  PERMISSION_ORG_SCOPE_MISSING: {
    title: "Organizasyon bağlamı belirlenemedi.",
    action: { kind: "reload", label: "Sayfayı yenile" },
  },
  PERMISSION_ORG_INSUFFICIENT: {
    title: "Bu organizasyon için yetkiniz yetersiz.",
    hint: "Organizasyon yöneticinizle görüşün.",
  },
  PERMISSION_ORG_ADMIN_REQUIRED: { title: "Bu işlemi yalnızca organizasyon yöneticisi yapabilir." },

  // Organizations
  ORG_NOT_FOUND: { title: "Organizasyon bulunamadı." },
  ORG_SLUG_TAKEN: {
    title: "Bu kısa ad zaten kullanımda.",
    hint: "Farklı bir kısa ad deneyin (örn. sonuna şehir ekleyin).",
  },
  ORG_OWNER_UNKNOWN: { title: "Belirtilen sahip kullanıcı bulunamadı." },
  ORG_SELF_SERVICE_DISABLED: { title: "Kendi kendine organizasyon oluşturma kapalı." },
  ORG_QUOTA_REACHED: { title: "En fazla organizasyon sayısına ulaştınız." },
  ORG_MAX_DEPTH_REACHED: { title: "Alt organizasyon iç içe geçme sınırına ulaşıldı." },
  ORG_MEMBER_NOT_FOUND: { title: "Üye bulunamadı." },
  ORG_MEMBER_EXISTS: { title: "Bu kişi zaten organizasyonun üyesi." },
  ORG_LAST_ADMIN_REMOVE: { title: "Son yönetici çıkarılamaz.", hint: "Önce başka bir üyeyi yönetici yapın." },
  ORG_LAST_ADMIN_DEMOTE: {
    title: "Son yöneticinin rolü düşürülemez.",
    hint: "Önce başka bir üyeyi yönetici yapın.",
  },
  ORG_LOCKED_UNPAID_INVOICE: {
    title: "Ödenmemiş fatura nedeniyle düzenleme kilitlendi.",
    hint: "Faturayı ödeyerek düzenlemeye devam edin.",
    action: { kind: "route", to: "/billing", label: "Faturalara git" },
  },
  ORG_VIEW_ALLOWANCE_EXCEEDED: {
    title: "Ücretsiz planın yıllık görüntüleme hakkı doldu.",
    hint: "Düzenlemeye devam etmek için planınızı yükseltin.",
    action: { kind: "route", to: "/billing", label: "Planı yükselt" },
  },

  // Users & sessions
  USER_NOT_FOUND: { title: "Kullanıcı bulunamadı." },
  USER_LAST_SUPERADMIN: { title: "Son süper yönetici bu işlemle kaldırılamaz." },
  USER_SUPERADMIN_GRANT_DENIED: { title: "Süper yönetici rolünü yalnızca bir süper yönetici verebilir." },
  USER_TARGET_OUTRANKS_ACTOR: {
    title: "Sizde olmayan yetkilere sahip bir hesap üzerinde işlem yapamazsınız.",
  },
  USER_SELF_PASSWORD_VIA_ADMIN: { title: "Kendi şifrenizi Profil sayfasından değiştirin." },
  USER_ROLE_ESCALATION_DENIED: { title: "Sahip olmadığınız yetkileri içeren bir rol atayamazsınız." },
  USER_ROLE_UNKNOWN: { title: "Bilinmeyen rol." },
  USER_SELF_DELETE_DENIED: { title: "Kendi hesabınızı silemezsiniz." },
  SESSION_NOT_FOUND: { title: "Oturum bulunamadı." },

  // Org invitations
  INVITE_NOT_FOUND: { title: "Davet bulunamadı." },
  INVITE_MEMBER_EXISTS: { title: "Bu kişi zaten organizasyonun üyesi." },
  INVITE_PENDING_EXISTS: { title: "Bu e-posta için bekleyen bir davet zaten var." },
  INVITE_NOT_REVOCABLE: { title: "Yalnızca bekleyen bir davet iptal edilebilir." },
  INVITE_EXPIRED: { title: "Bu davetin süresi dolmuş.", hint: "Yeni bir davet isteyin." },
  INVITE_EMAIL_MISMATCH: { title: "Bu davet farklı bir e-posta adresine gönderilmiş." },

  // Verification & reset
  VERIFICATION_LINK_INVALID: { title: "Doğrulama bağlantısı geçersiz veya kullanılmış." },
  VERIFICATION_CODE_INVALID: { title: "Kod geçersiz veya daha önce kullanılmış." },
  VERIFICATION_EXPIRED: {
    title: "Doğrulamanın süresi dolmuş.",
    hint: "Yeni bir doğrulama e-postası isteyin.",
  },
  VERIFICATION_ALREADY_USED: { title: "Bu doğrulama zaten kullanılmış." },
  RESET_LINK_INVALID: {
    title: "Sıfırlama bağlantısı geçersiz veya kullanılmış.",
    hint: "Yeni bir sıfırlama bağlantısı isteyin.",
  },
  RESET_LINK_EXPIRED: {
    title: "Sıfırlama bağlantısının süresi dolmuş.",
    hint: "Yeni bir sıfırlama bağlantısı isteyin.",
  },
  RESET_LINK_USED: { title: "Sıfırlama bağlantısı zaten kullanılmış." },

  // Products & identifiers
  PRODUCT_NOT_FOUND: { title: "Ürün bulunamadı." },
  PRODUCT_SKU_TAKEN: {
    title: "Bu SKU ile bir ürün bu organizasyonda zaten var.",
    hint: "Farklı bir SKU girin.",
  },
  PRODUCT_PLAN_LIMIT_REACHED: {
    title: "Ücretsiz planın ürün limiti doldu.",
    hint: "Daha fazla ürün eklemek için planınızı yükseltin.",
    action: { kind: "route", to: "/billing", label: "Planı yükselt" },
  },
  PRODUCT_GTIN_REQUIRED_FOR_GS1: {
    title: "GS1 tanımlayıcı için ürünün bir GTIN'i olmalı.",
    hint: "Önce ürüne bir GTIN ekleyin.",
  },
  PRODUCT_SERIAL_REQUIRED: { title: "Ürün-seviyesi GS1 tanımlayıcı bir seri numarası gerektirir." },
  PRODUCT_LOT_REQUIRED: { title: "Parti-seviyesi GS1 tanımlayıcı bir parti (lot) numarası gerektirir." },
  IDENTIFIER_QUALIFIER_NOT_ALLOWED: {
    title: "Bu ayrıntı düzeyi için parti/seri numarası girilemez.",
    hint: "Model düzeyi parti ve seri taşımaz; parti düzeyi yalnızca parti taşır.",
  },
  IDENTIFIER_NOT_FOUND: { title: "Tanımlayıcı bulunamadı." },
  IDENTIFIER_VALUE_TAKEN: { title: "Bu tanımlayıcı değeri zaten atanmış." },
  IDENTIFIER_WITHDRAWN: {
    title: "Geri çekilmiş bir tanımlayıcıya yeni pasaport bağlanamaz.",
    hint: "Ürüne yeni bir tanımlayıcı atayın.",
  },
  IDENTIFIER_ALREADY_WITHDRAWN: { title: "Bu tanımlayıcı zaten geri çekilmiş." },
  OPERATOR_NOT_FOUND: { title: "İşletmeci bulunamadı." },
  OPERATOR_IDENTIFIER_INVALID: {
    title: "İşletmeci tanımlayıcısı geçersiz.",
    hint: "GLN 13 hane ve kontrol hanesi doğru olmalı.",
  },
  OPERATOR_ROLE_MISMATCH: { title: "İşletmecinin rolü bu atamayla uyuşmuyor." },
  OPERATOR_IN_USE: {
    title: "İşletmeci hâlâ ürünlere atanmış.",
    hint: "Önce ürünlerdeki atamayı kaldırın.",
  },
  FACILITY_IDENTIFIER_INVALID: {
    title: "Tesis tanımlayıcısı geçersiz.",
    hint: "GLN 13 hane ve kontrol hanesi doğru olmalı; şema ve değer birlikte verilmeli.",
  },
  DOCUMENT_NOT_FOUND: { title: "Belge bulunamadı." },
  DOCUMENT_UNSUPPORTED_FORMAT: { title: "Yalnızca PDF belgeler kabul edilir." },
  DOCUMENT_TOO_LARGE: { title: "Belge 20 MB sınırını aşıyor." },
  DOCUMENT_LIMIT_REACHED: { title: "Ürün başına belge sınırına ulaşıldı (20)." },
  DOCUMENT_RAW_BYTES_REQUIRED: { title: "PDF baytları application/pdf içerik türüyle gönderilmeli." },
  DOCUMENT_ALREADY_WITHDRAWN: { title: "Bu belge zaten geri çekilmiş." },
  DOCUMENT_SUPERSEDES_INVALID: { title: "Yenilenen belge aynı ürüne ait olmalı." },
  PASSPORT_READINESS_BLOCKED: {
    title: "Hazırlık kuralları yayını engelliyor.",
    hint: "Ürün sayfasındaki hazırlık kontrollerini giderin.",
  },
  PASSPORT_MARKET_DATES_INVALID: { title: "Son birimin arz tarihi ilk arz tarihinden önce olamaz." },
  ORG_SUSPENDED: {
    title: "Organizasyon askıya alındı — yazma erişimi kapalı.",
    hint: "Verileriniz okunabilir ve kamuya açık pasaportlar yayında kalır; platform yöneticisine başvurun.",
  },
  ORG_WOUND_DOWN: {
    title: "Organizasyon tasfiye edildi — yazma erişimi kapalı.",
    hint: "Kamuya açık pasaportlar yayında kalır; değişiklik için platform yöneticisine başvurun.",
  },
  BACKUP_NOT_CONFIGURED: { title: "Yedek barındırıcı yapılandırılmamış." },
  PASSPORT_LINEAGE_INVALID: {
    title: "Soy bağı geçersiz.",
    hint: "Orijinali ya kimlikle ya da URL ile belirtin; pasaport kendisinden türeyemez.",
  },
  DISPOSAL_REPORT_NOT_FOUND: { title: "Beyan bulunamadı." },
  DISPOSAL_REPORT_PUBLISHED: { title: "Yayımlanmış beyan dondurulmuştur; değiştirilemez." },
  DISPOSAL_SHARES_INVALID: { title: "Geri kazanım payları toplamı %100 olmalı." },

  // CSV import
  IMPORT_CSV_MALFORMED: {
    title: "CSV dosyası okunamadı.",
    hint: "Dosya biçimini kontrol edip tekrar deneyin.",
  },
  IMPORT_CSV_EMPTY: { title: "CSV dosyası boş." },
  IMPORT_CSV_HEADER_MISSING: {
    title: "CSV başlığında zorunlu bir sütun eksik.",
    hint: "sku, name ve schemaId sütunları zorunludur.",
  },
  IMPORT_ROW_SKU_REQUIRED: { title: "Satırda sku zorunlu." },
  IMPORT_ROW_NAME_REQUIRED: { title: "Satırda name zorunlu." },
  IMPORT_ROW_SCHEMA_REQUIRED: { title: "Satırda schemaId zorunlu." },

  // Passports
  PASSPORT_NOT_FOUND: { title: "Pasaport bulunamadı." },
  PASSPORT_VERSION_NOT_FOUND: { title: "Pasaport sürümü bulunamadı." },
  PASSPORT_NOT_COMPLIANT: {
    title: "Ürün nitelikleri yayına uygun değil.",
    hint: "İşaretli zorunlu alanları tamamlayın.",
  },
  PASSPORT_ARCHIVED_CANNOT_PUBLISH: { title: "Arşivlenmiş bir pasaport yayınlanamaz." },
  PASSPORT_ARCHIVED_DURING_PUBLISH: { title: "Pasaport yayınlanırken arşivlendi." },
  PASSPORT_PUBLISH_FAILED: {
    title: "Pasaport yayınlanamadı.",
    hint: "Tekrar deneyin.",
    action: { kind: "retry", label: "Tekrar dene" },
  },
  PASSPORT_QR_REQUIRES_PUBLISHED: { title: "Yalnızca yayınlanmış bir pasaportun QR hedefi olur." },
  SCHEMA_UNKNOWN: { title: "Bilinmeyen pasaport şeması." },

  // Media
  MEDIA_NOT_FOUND: { title: "Görsel bulunamadı." },
  MEDIA_RAW_BYTES_REQUIRED: {
    title: "Görselin ham baytlarını gönderin.",
    hint: "JPEG, PNG veya WebP biçiminde bir görsel yükleyin.",
  },
  MEDIA_UNSUPPORTED_FORMAT: { title: "Desteklenmeyen görsel biçimi.", hint: "JPEG, PNG veya WebP kullanın." },
  MEDIA_TOO_LARGE: { title: "Görsel boyut sınırını aşıyor.", hint: "Daha küçük bir görsel yükleyin." },
  MEDIA_LIMIT_REACHED: {
    title: "Bu ürün için görsel sınırına ulaşıldı.",
    hint: "Yeni görsel için önce mevcut bir görseli silin.",
  },

  // Supplier data collection
  SUPPLIER_INVITE_NOT_FOUND: { title: "Tedarikçi daveti bulunamadı." },
  SUPPLIER_LINK_INVALID: {
    title: "Bu bağlantı geçersiz, süresi dolmuş veya artık aktif değil.",
    hint: "Sizi davet eden firmadan yeni bir bağlantı isteyin.",
  },
  SUPPLIER_FIELDS_UNKNOWN: { title: "İstenen bazı alanlar ürünün şemasında yok." },
  SUPPLIER_GROUP_FIELDS_UNSUPPORTED: { title: "Grup değerli alanlar tedarikçiden istenemez." },
  SUPPLIER_INVITE_NOT_REVOCABLE: { title: "Bu davet artık iptal edilemez." },
  SUPPLIER_SUBMISSION_NOT_FOUND: { title: "Bu davet için gönderim yok." },
  SUPPLIER_SUBMISSION_NOT_APPLICABLE: { title: "Bu davetin uygulanabilir bir gönderimi yok." },
  SUPPLIER_SUBMISSION_ALREADY_APPLIED: { title: "Bu gönderim zaten uygulandı." },
  SUPPLIER_FIELDS_NOT_IN_SUBMISSION: { title: "Bazı alanlar bu gönderimin parçası değil." },

  // Access grants
  ACCESS_GRANT_NOT_FOUND: { title: "Erişim izni bulunamadı." },
  ACCESS_GRANT_NOT_REVOCABLE: { title: "Bu erişim izni artık iptal edilemez." },

  // Machine integration
  APIKEY_NOT_FOUND: { title: "API anahtarı bulunamadı." },
  APIKEY_NOT_REVOCABLE: { title: "Bu API anahtarı artık iptal edilemez." },
  WEBHOOK_NOT_FOUND: { title: "Webhook bulunamadı." },
  WEBHOOK_DELIVERY_NOT_FOUND: { title: "Teslimat kaydı bulunamadı." },
  WEBHOOK_DELIVERY_NOT_RETRIABLE: { title: "Yalnızca başarısız bir teslimat yeniden denenebilir." },
  WEBHOOK_DELIVERY_ATTEMPT_LIMIT: { title: "Teslimat deneme sınırına ulaşıldı." },
  URL_INVALID: { title: "URL geçerli değil.", hint: "Geçerli bir adres girin (https://…)." },
  URL_SCHEME_UNSUPPORTED: { title: "URL http veya https kullanmalı." },
  URL_HTTPS_REQUIRED: { title: "URL https kullanmalı." },
  URL_UNRESOLVABLE: { title: "URL'nin ana bilgisayar adı çözümlenemedi." },
  URL_PRIVATE_ADDRESS: {
    title: "URL herkese açık bir adrese çözümlenmeli.",
    hint: "İç/özel ağ adresleri kabul edilmez.",
  },

  // Supply chain master data
  FACILITY_NOT_FOUND: { title: "Tesis bulunamadı." },
  CERTIFICATION_NOT_FOUND: { title: "Sertifika bulunamadı." },
  LCA_NOT_FOUND: { title: "YDA (LCA) sonucu bulunamadı." },
  PARTNER_NOT_FOUND: { title: "İş ortağı bulunamadı." },

  // Billing
  INVOICE_NOT_FOUND: { title: "Fatura bulunamadı." },
  INVOICE_NOT_ISSUABLE: { title: "Yalnızca taslak bir fatura kesilebilir." },
  INVOICE_NOT_PAYABLE: { title: "Yalnızca kesilmiş veya gecikmiş bir fatura ödendi olarak işaretlenebilir." },
  INVOICE_PAID_CANNOT_VOID: { title: "Ödenmiş bir fatura iptal edilemez." },

  // Reviews
  REVIEW_NOT_FOUND: { title: "Değerlendirme bulunamadı." },
  REVIEW_RATE_LIMITED: {
    title: "Bu ürün için şu an çok fazla değerlendirme var.",
    hint: "Bir süre sonra tekrar deneyin.",
    action: { kind: "retry", label: "Tekrar dene" },
  },

  // Registry
  REGISTRY_NOT_CONFIGURED: { title: "EU DPP Kayıt Sistemi yapılandırılmamış." },
  REGISTRY_PASSPORT_NOT_PUBLISHED: { title: "Yalnızca yayınlanmış bir pasaport kaydedilebilir." },
  REGISTRY_ALREADY_REGISTERED: { title: "Pasaport zaten EU DPP Kayıt Sistemi'nde kayıtlı." },
  REGISTRY_REGISTRATION_NOT_FOUND: { title: "Kayıt bulunamadı." },
  REGISTRY_REGISTRATION_IN_PROGRESS: {
    title: "Gönderim hâlâ registry tarafından doğrulanıyor.",
    hint: "Sonuç geldiğinde durum kendiliğinden güncellenir.",
  },
  REGISTRY_ENROLMENT_MISSING: {
    title: "Kuruluş, EU DPP Registry'de ekonomik operatör olarak kayıtlı değil.",
    hint: "Önce ekonomik operatör bilgilerini kaydedin.",
  },
  REGISTRY_ENROLMENT_NOT_VERIFIED: {
    title: "Ekonomik operatör doğrulaması henüz tamamlanmadı.",
    hint: "Komisyon'un mühürlü PDF beyanını QES/QSeal ile imzalayıp registry'ye yükleyin.",
  },
  REGISTRY_VERIFICATION_EXPIRED: {
    title: "Ekonomik operatör doğrulamasının süresi doldu.",
    hint: "Doğrulama üç yıl geçerlidir; registry üzerinden yenileyin.",
  },
  REGISTRY_PRODUCT_GROUP_UNSUPPORTED: {
    title: "Bu ürün grubu için registry henüz kayıt açmadı.",
    hint: "Şu an yalnızca bataryalar kaydedilebiliyor.",
  },
  REGISTRY_UPI_INVALID: {
    title: "Pasaportun genel tanımlayıcısı kaydedilebilir değil.",
    hint: "UPI https ile başlamalı ve en fazla 50 karakter olmalı.",
  },

  // Licensing
  LICENSE_READ_ONLY: {
    title: "Lisans durumu nedeniyle sistem salt-okunur.",
    hint: "Lisansınızı yenileyin.",
    action: { kind: "route", to: "/license", label: "Lisansa git" },
  },
  LICENSE_FEATURE_NOT_INCLUDED: {
    title: "Bu özellik lisans sürümünüze dahil değil.",
    action: { kind: "route", to: "/license", label: "Lisansa git" },
  },
  LICENSE_SEAT_LIMIT: {
    title: "Lisansınızın kullanıcı (koltuk) sınırına ulaşıldı.",
    action: { kind: "route", to: "/license", label: "Lisansa git" },
  },
  LICENSE_PUBLIC_KEY_MISSING: { title: "Sunucuda sağlayıcı ortak anahtarı yüklü değil." },
  LICENSE_SIGNATURE_INVALID: { title: "Sağlanan lisans imza doğrulamasını geçemedi." },

  // Client-only (transport)
  NETWORK_UNREACHABLE: {
    title: "Sunucuya ulaşılamıyor.",
    hint: "İnternet bağlantınızı kontrol edip tekrar deneyin.",
    action: { kind: "retry", label: "Tekrar dene" },
  },
  NETWORK_TIMEOUT: {
    title: "İstek zaman aşımına uğradı.",
    hint: "Tekrar deneyin.",
    action: { kind: "retry", label: "Tekrar dene" },
  },
  SERVER_UNAVAILABLE: {
    title: "Sunucu şu an yanıt vermiyor.",
    hint: "Birkaç dakika sonra tekrar deneyin.",
    action: { kind: "retry", label: "Tekrar dene" },
  },
  UNKNOWN_ERROR: {
    title: "Beklenmeyen bir hata oluştu.",
    action: { kind: "reload", label: "Sayfayı yenile" },
  },
};

const en: Record<ErrorReason, ErrorMessage> = {
  VALIDATION_FAILED: {
    title: "There are problems with the information you submitted.",
    hint: "Fix the highlighted fields and try again.",
  },
  REQUEST_ROUTE_NOT_FOUND: { title: "The requested address was not found." },
  REQUEST_BODY_MALFORMED: {
    title: "The request data could not be read.",
    hint: "Reload the page and try again.",
    action: { kind: "reload", label: "Reload page" },
  },
  REQUEST_PAYLOAD_TOO_LARGE: {
    title: "The content you sent is too large.",
    hint: "Try a smaller file or less data.",
  },
  REQUEST_UNSUPPORTED_MEDIA_TYPE: { title: "This content type is not supported." },
  REQUEST_RATE_LIMITED: {
    title: "Too many requests.",
    hint: "Wait a moment and try again.",
    action: { kind: "retry", label: "Try again" },
  },
  REQUEST_CORS_REJECTED: { title: "The request origin is not allowed by this server." },
  INTERNAL_ERROR: {
    title: "An unexpected error occurred.",
    hint: "If it persists, share the support reference.",
    action: { kind: "reload", label: "Reload page" },
  },

  AUTH_REQUIRED: { title: "You must sign in to do this.", action: { kind: "login", label: "Sign in" } },
  AUTH_INVALID_CREDENTIALS: {
    title: "Email or password is incorrect.",
    hint: "Check your details and try again.",
  },
  AUTH_CURRENT_PASSWORD_INVALID: { title: "Your current password is incorrect." },
  AUTH_PASSWORD_INVALID: { title: "Password is incorrect." },
  AUTH_ACCOUNT_LOCKED: {
    title: "Your account is temporarily locked.",
    hint: "Too many failed attempts. Try again later.",
  },
  AUTH_PASSWORD_REUSED: {
    title: "You used this password recently.",
    hint: "Choose a password you haven't used before.",
  },
  AUTH_SELF_REGISTRATION_DISABLED: {
    title: "Self-registration is disabled.",
    hint: "Contact your organization admin for an account.",
  },
  AUTH_EMAIL_TAKEN: {
    title: "An account with this email already exists.",
    hint: "Try signing in or use a different email.",
  },
  AUTH_TOKEN_INVALID: {
    title: "Your session is invalid.",
    hint: "Please sign in again.",
    action: { kind: "login", label: "Sign in" },
  },
  AUTH_ACCESS_TOKEN_EXPIRED: {
    title: "Your session has expired.",
    action: { kind: "login", label: "Sign in again" },
  },
  AUTH_REFRESH_TOKEN_UNKNOWN: {
    title: "Session not recognized.",
    hint: "Please sign in again.",
    action: { kind: "login", label: "Sign in" },
  },
  AUTH_REFRESH_TOKEN_EXPIRED: {
    title: "Your session has expired.",
    action: { kind: "login", label: "Sign in again" },
  },
  AUTH_SESSION_EXPIRED: {
    title: "Your session has ended.",
    hint: "Please sign in again for security.",
    action: { kind: "login", label: "Sign in again" },
  },
  AUTH_TOKEN_REUSE_DETECTED: {
    title: "Your session was ended for security.",
    hint: "Please sign in again.",
    action: { kind: "login", label: "Sign in" },
  },
  AUTH_MFA_CODE_INVALID: {
    title: "The verification code is incorrect.",
    hint: "Enter the 6-digit code from your authenticator app.",
  },
  AUTH_MFA_CHALLENGE_EXPIRED: {
    title: "The verification timed out.",
    hint: "Please sign in again.",
    action: { kind: "login", label: "Sign in" },
  },
  AUTH_MFA_ENROLLMENT_REQUIRED: {
    title: "This action requires two-factor authentication.",
    hint: "Set up 2FA from your profile.",
    action: { kind: "route", to: "/profile", label: "Set up 2FA" },
  },
  AUTH_MFA_ENROLLMENT_NOT_STARTED: { title: "Start two-factor enrollment first." },
  AUTH_MFA_ALREADY_ENABLED: {
    title: "Two-factor authentication is already on.",
    hint: "Disable it before enrolling again.",
  },
  AUTH_PASSWORD_CHANGE_SESSION_EXPIRED: {
    title: "The password-change session has expired.",
    action: { kind: "login", label: "Sign in again" },
  },
  AUTH_DEMO_READ_ONLY: {
    title: "This is a read-only demo account.",
    hint: "You can explore forms, but changes are not saved.",
  },
  AUTH_API_KEY_REQUIRED: { title: "An API key is required." },
  AUTH_API_KEY_INVALID: { title: "The API key is invalid." },
  AUTH_API_KEY_SCOPE_MISSING: { title: "The API key lacks the required scope for this action." },

  PERMISSION_DENIED: {
    title: "You don't have permission to do this.",
    hint: "Ask your admin for the required permission.",
  },
  PERMISSION_ORG_SCOPE_MISSING: {
    title: "The organization context could not be determined.",
    action: { kind: "reload", label: "Reload page" },
  },
  PERMISSION_ORG_INSUFFICIENT: {
    title: "Your permissions for this organization are insufficient.",
    hint: "Contact your organization admin.",
  },
  PERMISSION_ORG_ADMIN_REQUIRED: { title: "Only an organization admin can do this." },

  ORG_NOT_FOUND: { title: "Organization not found." },
  ORG_SLUG_TAKEN: {
    title: "This short name is already in use.",
    hint: "Try a different short name (e.g. append a city).",
  },
  ORG_OWNER_UNKNOWN: { title: "The specified owner user was not found." },
  ORG_SELF_SERVICE_DISABLED: { title: "Self-service organization creation is disabled." },
  ORG_QUOTA_REACHED: { title: "You've reached the maximum number of organizations." },
  ORG_MAX_DEPTH_REACHED: { title: "Maximum sub-organization nesting depth reached." },
  ORG_MEMBER_NOT_FOUND: { title: "Member not found." },
  ORG_MEMBER_EXISTS: { title: "This person is already a member of the organization." },
  ORG_LAST_ADMIN_REMOVE: {
    title: "The last admin can't be removed.",
    hint: "Make another member an admin first.",
  },
  ORG_LAST_ADMIN_DEMOTE: {
    title: "The last admin can't be demoted.",
    hint: "Make another member an admin first.",
  },
  ORG_LOCKED_UNPAID_INVOICE: {
    title: "Editing is locked due to an unpaid invoice.",
    hint: "Pay the invoice to keep editing.",
    action: { kind: "route", to: "/billing", label: "Go to billing" },
  },
  ORG_VIEW_ALLOWANCE_EXCEEDED: {
    title: "The free plan's yearly view allowance is used up.",
    hint: "Upgrade your plan to keep editing.",
    action: { kind: "route", to: "/billing", label: "Upgrade plan" },
  },

  USER_NOT_FOUND: { title: "User not found." },
  USER_LAST_SUPERADMIN: { title: "This action would remove the last superadmin." },
  USER_SUPERADMIN_GRANT_DENIED: { title: "Only a superadmin can grant the superadmin role." },
  USER_TARGET_OUTRANKS_ACTOR: {
    title: "You can't act on an account that holds permissions you don't have.",
  },
  USER_SELF_PASSWORD_VIA_ADMIN: { title: "Change your own password from your profile." },
  USER_ROLE_ESCALATION_DENIED: { title: "You can't grant a role with permissions you don't hold." },
  USER_ROLE_UNKNOWN: { title: "Unknown role." },
  USER_SELF_DELETE_DENIED: { title: "You can't delete your own account." },
  SESSION_NOT_FOUND: { title: "Session not found." },

  INVITE_NOT_FOUND: { title: "Invitation not found." },
  INVITE_MEMBER_EXISTS: { title: "This person is already a member of the organization." },
  INVITE_PENDING_EXISTS: { title: "A pending invitation for this email already exists." },
  INVITE_NOT_REVOCABLE: { title: "Only a pending invitation can be revoked." },
  INVITE_EXPIRED: { title: "This invitation has expired.", hint: "Request a new invitation." },
  INVITE_EMAIL_MISMATCH: { title: "This invitation was sent to a different email address." },

  VERIFICATION_LINK_INVALID: { title: "The verification link is invalid or already used." },
  VERIFICATION_CODE_INVALID: { title: "The code is invalid or has already been used." },
  VERIFICATION_EXPIRED: { title: "The verification has expired.", hint: "Request a new verification email." },
  VERIFICATION_ALREADY_USED: { title: "This verification has already been used." },
  RESET_LINK_INVALID: {
    title: "The reset link is invalid or already used.",
    hint: "Request a new reset link.",
  },
  RESET_LINK_EXPIRED: { title: "The reset link has expired.", hint: "Request a new reset link." },
  RESET_LINK_USED: { title: "The reset link has already been used." },

  PRODUCT_NOT_FOUND: { title: "Product not found." },
  PRODUCT_SKU_TAKEN: {
    title: "A product with this SKU already exists in this organization.",
    hint: "Enter a different SKU.",
  },
  PRODUCT_PLAN_LIMIT_REACHED: {
    title: "The free plan's product limit is reached.",
    hint: "Upgrade your plan to add more products.",
    action: { kind: "route", to: "/billing", label: "Upgrade plan" },
  },
  PRODUCT_GTIN_REQUIRED_FOR_GS1: {
    title: "A GS1 identifier requires the product to have a GTIN.",
    hint: "Add a GTIN to the product first.",
  },
  PRODUCT_SERIAL_REQUIRED: { title: "An item-level GS1 identifier requires a serial number." },
  PRODUCT_LOT_REQUIRED: { title: "A batch-level GS1 identifier requires a lot number." },
  IDENTIFIER_QUALIFIER_NOT_ALLOWED: {
    title: "A lot/serial cannot be given at this granularity.",
    hint: "Model level carries neither a lot nor a serial; batch level carries only a lot.",
  },
  IDENTIFIER_NOT_FOUND: { title: "Identifier not found." },
  IDENTIFIER_VALUE_TAKEN: { title: "This identifier value is already assigned." },
  IDENTIFIER_WITHDRAWN: {
    title: "A withdrawn identifier cannot anchor a new passport.",
    hint: "Assign a new identifier to the product.",
  },
  IDENTIFIER_ALREADY_WITHDRAWN: { title: "This identifier is already withdrawn." },
  OPERATOR_NOT_FOUND: { title: "Operator not found." },
  OPERATOR_IDENTIFIER_INVALID: {
    title: "The operator identifier is invalid.",
    hint: "A GLN is 13 digits with a valid check digit.",
  },
  OPERATOR_ROLE_MISMATCH: { title: "The operator's role does not match this assignment." },
  OPERATOR_IN_USE: {
    title: "The operator is still assigned to products.",
    hint: "Remove the product assignments first.",
  },
  FACILITY_IDENTIFIER_INVALID: {
    title: "The facility identifier is invalid.",
    hint: "A GLN is 13 digits with a valid check digit; scheme and value go together.",
  },
  DOCUMENT_NOT_FOUND: { title: "Document not found." },
  DOCUMENT_UNSUPPORTED_FORMAT: { title: "Only PDF documents are accepted." },
  DOCUMENT_TOO_LARGE: { title: "The document exceeds the 20 MB limit." },
  DOCUMENT_LIMIT_REACHED: { title: "Document limit reached for this product (20)." },
  DOCUMENT_RAW_BYTES_REQUIRED: { title: "Send the PDF bytes with content-type application/pdf." },
  DOCUMENT_ALREADY_WITHDRAWN: { title: "This document is already withdrawn." },
  DOCUMENT_SUPERSEDES_INVALID: { title: "The superseded document must belong to the same product." },
  PASSPORT_READINESS_BLOCKED: {
    title: "Readiness rules block publishing.",
    hint: "Resolve the readiness checks on the product page.",
  },
  PASSPORT_MARKET_DATES_INVALID: { title: "The last unit cannot be placed on the market before the first." },
  ORG_SUSPENDED: {
    title: "The organization is suspended — write access is closed.",
    hint: "Your data stays readable and public passports stay online; contact the platform operator.",
  },
  ORG_WOUND_DOWN: {
    title: "The organization has been wound down — write access is closed.",
    hint: "Public passports stay online; contact the platform operator for changes.",
  },
  BACKUP_NOT_CONFIGURED: { title: "The backup host is not configured." },
  PASSPORT_LINEAGE_INVALID: {
    title: "The lineage is invalid.",
    hint: "Name the original by id OR by URL; a passport cannot descend from itself.",
  },
  DISPOSAL_REPORT_NOT_FOUND: { title: "Disclosure not found." },
  DISPOSAL_REPORT_PUBLISHED: { title: "A published disclosure is frozen and cannot be changed." },
  DISPOSAL_SHARES_INVALID: { title: "Treatment shares must add up to 100 %." },

  IMPORT_CSV_MALFORMED: {
    title: "The CSV file could not be read.",
    hint: "Check the file format and try again.",
  },
  IMPORT_CSV_EMPTY: { title: "The CSV file is empty." },
  IMPORT_CSV_HEADER_MISSING: {
    title: "A required column is missing from the CSV header.",
    hint: "sku, name and schemaId columns are required.",
  },
  IMPORT_ROW_SKU_REQUIRED: { title: "sku is required in the row." },
  IMPORT_ROW_NAME_REQUIRED: { title: "name is required in the row." },
  IMPORT_ROW_SCHEMA_REQUIRED: { title: "schemaId is required in the row." },

  PASSPORT_NOT_FOUND: { title: "Passport not found." },
  PASSPORT_VERSION_NOT_FOUND: { title: "Passport version not found." },
  PASSPORT_NOT_COMPLIANT: {
    title: "Product attributes are not ready to publish.",
    hint: "Complete the highlighted required fields.",
  },
  PASSPORT_ARCHIVED_CANNOT_PUBLISH: { title: "An archived passport can't be published." },
  PASSPORT_ARCHIVED_DURING_PUBLISH: { title: "The passport was archived while publishing." },
  PASSPORT_PUBLISH_FAILED: {
    title: "The passport could not be published.",
    hint: "Try again.",
    action: { kind: "retry", label: "Try again" },
  },
  PASSPORT_QR_REQUIRES_PUBLISHED: { title: "Only a published passport has a QR target." },
  SCHEMA_UNKNOWN: { title: "Unknown passport schema." },

  MEDIA_NOT_FOUND: { title: "Image not found." },
  MEDIA_RAW_BYTES_REQUIRED: { title: "Send the raw image bytes.", hint: "Upload a JPEG, PNG or WebP image." },
  MEDIA_UNSUPPORTED_FORMAT: { title: "Unsupported image format.", hint: "Use JPEG, PNG or WebP." },
  MEDIA_TOO_LARGE: { title: "The image exceeds the size limit.", hint: "Upload a smaller image." },
  MEDIA_LIMIT_REACHED: {
    title: "The image limit for this product is reached.",
    hint: "Delete an existing image to add a new one.",
  },

  SUPPLIER_INVITE_NOT_FOUND: { title: "Supplier invite not found." },
  SUPPLIER_LINK_INVALID: {
    title: "This link is invalid, has expired, or is no longer active.",
    hint: "Ask the company that invited you for a new link.",
  },
  SUPPLIER_FIELDS_UNKNOWN: { title: "Some requested fields don't exist in the product's schema." },
  SUPPLIER_GROUP_FIELDS_UNSUPPORTED: { title: "Group-valued fields can't be requested from suppliers." },
  SUPPLIER_INVITE_NOT_REVOCABLE: { title: "This invite can no longer be revoked." },
  SUPPLIER_SUBMISSION_NOT_FOUND: { title: "No submission exists for this invite." },
  SUPPLIER_SUBMISSION_NOT_APPLICABLE: { title: "This invite has no applicable submission." },
  SUPPLIER_SUBMISSION_ALREADY_APPLIED: { title: "This submission has already been applied." },
  SUPPLIER_FIELDS_NOT_IN_SUBMISSION: { title: "Some fields are not part of this submission." },

  ACCESS_GRANT_NOT_FOUND: { title: "Access grant not found." },
  ACCESS_GRANT_NOT_REVOCABLE: { title: "This access grant can no longer be revoked." },

  APIKEY_NOT_FOUND: { title: "API key not found." },
  APIKEY_NOT_REVOCABLE: { title: "This API key can no longer be revoked." },
  WEBHOOK_NOT_FOUND: { title: "Webhook not found." },
  WEBHOOK_DELIVERY_NOT_FOUND: { title: "Delivery not found." },
  WEBHOOK_DELIVERY_NOT_RETRIABLE: { title: "Only a failed delivery can be retried." },
  WEBHOOK_DELIVERY_ATTEMPT_LIMIT: { title: "The delivery attempt limit is reached." },
  URL_INVALID: { title: "The URL is not valid.", hint: "Enter a valid address (https://…)." },
  URL_SCHEME_UNSUPPORTED: { title: "The URL must use http or https." },
  URL_HTTPS_REQUIRED: { title: "The URL must use https." },
  URL_UNRESOLVABLE: { title: "The URL's hostname could not be resolved." },
  URL_PRIVATE_ADDRESS: {
    title: "The URL must resolve to a public address.",
    hint: "Internal/private network addresses are not allowed.",
  },

  FACILITY_NOT_FOUND: { title: "Facility not found." },
  CERTIFICATION_NOT_FOUND: { title: "Certification not found." },
  LCA_NOT_FOUND: { title: "LCA result not found." },
  PARTNER_NOT_FOUND: { title: "Partner not found." },

  INVOICE_NOT_FOUND: { title: "Invoice not found." },
  INVOICE_NOT_ISSUABLE: { title: "Only a draft invoice can be issued." },
  INVOICE_NOT_PAYABLE: { title: "Only an issued or overdue invoice can be marked paid." },
  INVOICE_PAID_CANNOT_VOID: { title: "A paid invoice can't be voided." },

  REVIEW_NOT_FOUND: { title: "Review not found." },
  REVIEW_RATE_LIMITED: {
    title: "Too many reviews for this product right now.",
    hint: "Try again later.",
    action: { kind: "retry", label: "Try again" },
  },

  REGISTRY_NOT_CONFIGURED: { title: "The EU DPP Registry is not configured." },
  REGISTRY_PASSPORT_NOT_PUBLISHED: { title: "Only a published passport can be registered." },
  REGISTRY_ALREADY_REGISTERED: { title: "The passport is already registered in the EU DPP Registry." },
  REGISTRY_REGISTRATION_NOT_FOUND: { title: "Registration not found." },
  REGISTRY_REGISTRATION_IN_PROGRESS: {
    title: "The registry is still validating this submission.",
    hint: "The status updates itself once the outcome arrives.",
  },
  REGISTRY_ENROLMENT_MISSING: {
    title: "The organization is not enrolled as an economic operator.",
    hint: "Save the economic operator details first.",
  },
  REGISTRY_ENROLMENT_NOT_VERIFIED: {
    title: "The economic operator verification is not complete yet.",
    hint: "Seal the Commission's PDF declaration with a QES/QSeal and upload it to the registry.",
  },
  REGISTRY_VERIFICATION_EXPIRED: {
    title: "The economic operator verification has expired.",
    hint: "Verification lasts three years; renew it in the registry.",
  },
  REGISTRY_PRODUCT_GROUP_UNSUPPORTED: {
    title: "The registry has not opened registration for this product group.",
    hint: "Only batteries can be registered today.",
  },
  REGISTRY_UPI_INVALID: {
    title: "The passport's public identifier is not registrable.",
    hint: "The UPI must be https and at most 50 characters.",
  },

  LICENSE_READ_ONLY: {
    title: "The system is read-only due to license status.",
    hint: "Renew your license.",
    action: { kind: "route", to: "/license", label: "Go to license" },
  },
  LICENSE_FEATURE_NOT_INCLUDED: {
    title: "This feature is not included in your license edition.",
    action: { kind: "route", to: "/license", label: "Go to license" },
  },
  LICENSE_SEAT_LIMIT: {
    title: "Your license's user (seat) limit is reached.",
    action: { kind: "route", to: "/license", label: "Go to license" },
  },
  LICENSE_PUBLIC_KEY_MISSING: { title: "No vendor public key is installed on the server." },
  LICENSE_SIGNATURE_INVALID: { title: "The provided license failed signature verification." },

  NETWORK_UNREACHABLE: {
    title: "Can't reach the server.",
    hint: "Check your connection and try again.",
    action: { kind: "retry", label: "Try again" },
  },
  NETWORK_TIMEOUT: {
    title: "The request timed out.",
    hint: "Try again.",
    action: { kind: "retry", label: "Try again" },
  },
  SERVER_UNAVAILABLE: {
    title: "The server is not responding right now.",
    hint: "Try again in a few minutes.",
    action: { kind: "retry", label: "Try again" },
  },
  UNKNOWN_ERROR: { title: "An unexpected error occurred.", action: { kind: "reload", label: "Reload page" } },
};

export const REASON_MESSAGES: Record<Lang, Record<ErrorReason, ErrorMessage>> = { tr, en };

/* --- Coarse fallback by HTTP-class code ------------------------------------ */
// Used only when a response has no `reason` (older server, or a reason the
// client doesn't yet know). Keyed by the loose `error.code` string.

export const CODE_MESSAGES: Record<Lang, Record<string, ErrorMessage>> = {
  tr: {
    VALIDATION_ERROR: tr.VALIDATION_FAILED,
    UNAUTHENTICATED: tr.AUTH_REQUIRED,
    INVALID_CREDENTIALS: tr.AUTH_INVALID_CREDENTIALS,
    PASSWORD_EXPIRED: { title: "Parolanızın süresi doldu ve değiştirilmeli." },
    PASSWORD_REUSED: tr.AUTH_PASSWORD_REUSED,
    MFA_REQUIRED: { title: "İki adımlı doğrulama gerekiyor." },
    MFA_ENROLLMENT_REQUIRED: tr.AUTH_MFA_ENROLLMENT_REQUIRED,
    MFA_INVALID: tr.AUTH_MFA_CODE_INVALID,
    FORBIDDEN: tr.PERMISSION_DENIED,
    DEMO_READ_ONLY: tr.AUTH_DEMO_READ_ONLY,
    NOT_FOUND: { title: "Kayıt bulunamadı." },
    CONFLICT: { title: "İşlem mevcut durumla çakışıyor." },
    PAYMENT_REQUIRED: {
      title: "Bu işlem için ödeme gerekiyor.",
      action: { kind: "route", to: "/billing", label: "Faturalara git" },
    },
    RATE_LIMITED: tr.REQUEST_RATE_LIMITED,
    ACCOUNT_LOCKED: tr.AUTH_ACCOUNT_LOCKED,
    TOKEN_EXPIRED: tr.AUTH_ACCESS_TOKEN_EXPIRED,
    TOKEN_INVALID: tr.AUTH_TOKEN_INVALID,
    TOKEN_REUSE_DETECTED: tr.AUTH_TOKEN_REUSE_DETECTED,
    LICENSE_INVALID: { title: "Geçerli bir lisans yüklü değil." },
    LICENSE_EXPIRED: { title: "Lisansın süresi dolmuş." },
    LICENSE_READ_ONLY: tr.LICENSE_READ_ONLY,
    LICENSE_SEAT_LIMIT: tr.LICENSE_SEAT_LIMIT,
    FEATURE_NOT_LICENSED: tr.LICENSE_FEATURE_NOT_INCLUDED,
    INTERNAL_ERROR: tr.INTERNAL_ERROR,
    SERVER_UNAVAILABLE: tr.SERVER_UNAVAILABLE,
    NETWORK_ERROR: tr.NETWORK_UNREACHABLE,
    TIMEOUT: tr.NETWORK_TIMEOUT,
    HTTP_ERROR: tr.UNKNOWN_ERROR,
  },
  en: {
    VALIDATION_ERROR: en.VALIDATION_FAILED,
    UNAUTHENTICATED: en.AUTH_REQUIRED,
    INVALID_CREDENTIALS: en.AUTH_INVALID_CREDENTIALS,
    PASSWORD_EXPIRED: { title: "Your password has expired and must be changed." },
    PASSWORD_REUSED: en.AUTH_PASSWORD_REUSED,
    MFA_REQUIRED: { title: "Two-factor authentication is required." },
    MFA_ENROLLMENT_REQUIRED: en.AUTH_MFA_ENROLLMENT_REQUIRED,
    MFA_INVALID: en.AUTH_MFA_CODE_INVALID,
    FORBIDDEN: en.PERMISSION_DENIED,
    DEMO_READ_ONLY: en.AUTH_DEMO_READ_ONLY,
    NOT_FOUND: { title: "Record not found." },
    CONFLICT: { title: "The action conflicts with the current state." },
    PAYMENT_REQUIRED: {
      title: "This action requires payment.",
      action: { kind: "route", to: "/billing", label: "Go to billing" },
    },
    RATE_LIMITED: en.REQUEST_RATE_LIMITED,
    ACCOUNT_LOCKED: en.AUTH_ACCOUNT_LOCKED,
    TOKEN_EXPIRED: en.AUTH_ACCESS_TOKEN_EXPIRED,
    TOKEN_INVALID: en.AUTH_TOKEN_INVALID,
    TOKEN_REUSE_DETECTED: en.AUTH_TOKEN_REUSE_DETECTED,
    LICENSE_INVALID: { title: "No valid license is installed." },
    LICENSE_EXPIRED: { title: "The license has expired." },
    LICENSE_READ_ONLY: en.LICENSE_READ_ONLY,
    LICENSE_SEAT_LIMIT: en.LICENSE_SEAT_LIMIT,
    FEATURE_NOT_LICENSED: en.LICENSE_FEATURE_NOT_INCLUDED,
    INTERNAL_ERROR: en.INTERNAL_ERROR,
    SERVER_UNAVAILABLE: en.SERVER_UNAVAILABLE,
    NETWORK_ERROR: en.NETWORK_UNREACHABLE,
    TIMEOUT: en.NETWORK_TIMEOUT,
    HTTP_ERROR: en.UNKNOWN_ERROR,
  },
};

/** Last-resort generic message when neither reason nor code is mapped. */
export const GENERIC_MESSAGE: Record<Lang, ErrorMessage> = {
  tr: { title: "Bir şeyler ters gitti.", action: { kind: "reload", label: "Sayfayı yenile" } },
  en: { title: "Something went wrong.", action: { kind: "reload", label: "Reload page" } },
};

/* --- Field-level messages -------------------------------------------------- */

/**
 * Generic per-rule field messages. `{min}`/`{max}` are filled from the issue
 * params. Used when a field has no field-specific override below.
 */
export const RULE_MESSAGES: Record<Lang, Record<FieldRule, (p: FieldIssueParams) => string>> = {
  tr: {
    required: () => "Bu alan zorunludur.",
    too_short: (p) => (p.min && p.min > 1 ? `En az ${p.min} karakter olmalı.` : "Bu alan zorunludur."),
    too_long: (p) => `En fazla ${p.max} karakter olabilir.`,
    min: (p) => `En az ${p.min} olmalı.`,
    max: (p) => `En fazla ${p.max} olabilir.`,
    not_int: () => "Tam sayı olmalı.",
    invalid_type: () => "Geçersiz değer.",
    email: () => "Geçerli bir e-posta adresi girin.",
    url: () => "Geçerli bir adres girin (https://…).",
    uuid: () => "Geçerli bir kimlik değeri değil.",
    regex: () => "Biçim geçerli değil.",
    enum: (p) => (p.options?.length ? `Şunlardan biri olmalı: ${p.options.join(", ")}.` : "Geçersiz seçim."),
    date: () => "Geçerli bir tarih girin.",
    custom: () => "Bu değer kabul edilmedi.",
  },
  en: {
    required: () => "This field is required.",
    too_short: (p) =>
      p.min && p.min > 1 ? `Must be at least ${p.min} characters.` : "This field is required.",
    too_long: (p) => `Must be at most ${p.max} characters.`,
    min: (p) => `Must be at least ${p.min}.`,
    max: (p) => `Must be at most ${p.max}.`,
    not_int: () => "Must be a whole number.",
    invalid_type: () => "Invalid value.",
    email: () => "Enter a valid email address.",
    url: () => "Enter a valid address (https://…).",
    uuid: () => "Not a valid identifier.",
    regex: () => "The format is not valid.",
    enum: (p) => (p.options?.length ? `Must be one of: ${p.options.join(", ")}.` : "Invalid choice."),
    date: () => "Enter a valid date.",
    custom: () => "This value was not accepted.",
  },
};

/**
 * Field-specific overrides: a domain field can phrase a rule more helpfully than
 * the generic one (e.g. slug's regex rule explains the allowed characters).
 * Keyed by the field path the server/schema uses.
 */
export const FIELD_MESSAGES: Record<Lang, Record<string, Partial<Record<FieldRule, string>>>> = {
  tr: {
    slug: {
      regex: "Yalnızca küçük harf, rakam ve tire içerebilir.",
      too_short: "En az 2 karakter olmalı.",
    },
    gtin: { regex: "GTIN 8, 12, 13 veya 14 haneli olmalı." },
    gs1CompanyPrefix: { regex: "GS1 firma öneki 4–12 haneli olmalı." },
    commodityCode: { regex: "Gümrük kodu 8–10 haneli olmalı (CN/TARIC)." },
    accentColor: { regex: "Renk #rrggbb biçiminde olmalı." },
    countryIso: { regex: "ISO 3166-1 alpha-2 ülke kodu (örn. TR)." },
    url: { url: "Geçerli bir adres girin (https://…)." },
  },
  en: {
    slug: {
      regex: "May contain only lowercase letters, digits and hyphens.",
      too_short: "Must be at least 2 characters.",
    },
    gtin: { regex: "GTIN must be 8, 12, 13 or 14 digits." },
    gs1CompanyPrefix: { regex: "GS1 company prefix must be 4–12 digits." },
    commodityCode: { regex: "Commodity code must be 8–10 digits (CN/TARIC)." },
    accentColor: { regex: "Color must be in #rrggbb format." },
    countryIso: { regex: "ISO 3166-1 alpha-2 country code (e.g. TR)." },
    url: { url: "Enter a valid address (https://…)." },
  },
};

/** Human labels for common field paths, used to name a field in an error list. */
export const FIELD_LABELS: Record<Lang, Record<string, string>> = {
  tr: {
    email: "E-posta",
    password: "Parola",
    newPassword: "Yeni parola",
    currentPassword: "Mevcut parola",
    name: "Ad",
    displayName: "Görünen ad",
    slug: "Kısa ad",
    sku: "SKU",
    gtin: "GTIN",
    gs1CompanyPrefix: "GS1 firma öneki",
    commodityCode: "Gümrük kodu",
    schemaId: "Şema",
    url: "URL",
    accentColor: "Vurgu rengi",
    countryIso: "Ülke",
    role: "Rol",
    supplierName: "Tedarikçi adı",
    supplierEmail: "Tedarikçi e-postası",
    holderName: "Sahip adı",
  },
  en: {
    email: "Email",
    password: "Password",
    newPassword: "New password",
    currentPassword: "Current password",
    name: "Name",
    displayName: "Display name",
    slug: "Short name",
    sku: "SKU",
    gtin: "GTIN",
    gs1CompanyPrefix: "GS1 company prefix",
    commodityCode: "Commodity code",
    schemaId: "Schema",
    url: "URL",
    accentColor: "Accent color",
    countryIso: "Country",
    role: "Role",
    supplierName: "Supplier name",
    supplierEmail: "Supplier email",
    holderName: "Holder name",
  },
};
